// =============================================================================
// Tenant Switcher - Menu cambio organizzazione
// =============================================================================

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Check, Plus, Mail, X, Loader2, Building2 } from 'lucide-react';
import { useTenantStore, TenantMembership } from '../store/tenantStore';
import { CreateTenantModal } from './CreateTenantModal';
import { OrgAvatar } from './common/OrgAvatar';
import {
  useMyInvites,
  useAcceptMyInvite,
  useDeclineMyInvite,
  useMyDeletedTenants,
  refreshMyMemberships,
  MyInvite,
} from '../api/hooks';
import { useToast } from '../store/toastStore';

export function TenantSwitcher() {
  const { t } = useTranslation('common');
  const { activeTenantId, activeTenantName, memberships, selectTenant } = useTenantStore();
  const [isOpen, setIsOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const navigate = useNavigate();

  // Organizzazioni eliminate ma ripristinabili: il badge serve a far sapere
  // che esistono, altrimenti la schermata di ripristino non la cerca nessuno.
  const { data: deletedTenants = [] } = useMyDeletedTenants();

  // Inviti pendenti destinati alla mia email (badge + sezione nel dropdown)
  const { data: invites = [] } = useMyInvites();
  const acceptInvite = useAcceptMyInvite();
  const declineInvite = useDeclineMyInvite();
  const inviteBusy = acceptInvite.isPending || declineInvite.isPending;

  const handleAcceptInvite = async (invite: MyInvite) => {
    try {
      await acceptInvite.mutateAsync(invite.id);
      // La nuova organizzazione compare tra quelle disponibili, senza switch forzato
      await refreshMyMemberships();
      toast.success(t('tenant.inviteAccepted', { name: invite.tenantName }));
    } catch (err: any) {
      const msg = err.response?.data?.message;
      toast.error(t('tenant.inviteError'), typeof msg === 'string' ? msg : undefined);
    }
  };

  const handleDeclineInvite = async (invite: MyInvite) => {
    try {
      await declineInvite.mutateAsync(invite.id);
      toast.info(t('tenant.inviteDeclined', { name: invite.tenantName }));
    } catch (err: any) {
      const msg = err.response?.data?.message;
      toast.error(t('tenant.inviteError'), typeof msg === 'string' ? msg : undefined);
    }
  };

  // Chiudi dropdown con click fuori
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Chiudi dropdown con Escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsOpen(false);
    }
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen]);

  const handleSwitch = (membership: TenantMembership) => {
    if (membership.organizationId === activeTenantId) {
      setIsOpen(false);
      return;
    }
    selectTenant(membership);
    setIsOpen(false);
    window.location.href = '/scheduler';
  };

  const otherTenants = memberships.filter(
    (m) => m.organizationId !== activeTenantId,
  );

  // Il branding dell'organizzazione attiva vive nelle memberships persistite
  const activeBranding = memberships.find(
    (m) => m.organizationId === activeTenantId,
  )?.branding;

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        {/* Trigger */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="relative flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-200 transition-colors max-w-[200px]"
        >
          <OrgAvatar branding={activeBranding} name={activeTenantName} size="xs" />
          <span className="truncate">{activeTenantName || t('tenant.select')}</span>
          <ChevronDown
            className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
          {invites.length > 0 && (
            <span
              className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-amber-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
              title={t('tenant.pendingInvites')}
            >
              {invites.length}
            </span>
          )}
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute left-0 top-full mt-1 w-72 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-50 animate-in fade-in slide-in-from-top-1 duration-150">
            {/* Tenant attivo */}
            <div className="px-3 py-2">
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                {t('tenant.activeTenant')}
              </p>
            </div>
            <div className="mx-1 px-3 py-2.5 rounded-lg bg-emerald-50 flex items-center gap-3">
              <OrgAvatar branding={activeBranding} name={activeTenantName} size="sm" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {activeTenantName}
                </p>
                <p className="text-xs text-emerald-600">
                  {t(`roles.${useTenantStore.getState().activeRole || ''}`, { defaultValue: '' })}
                </p>
              </div>
              <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            </div>

            {/* Altri tenant */}
            {otherTenants.length > 0 && (
              <>
                <div className="border-t border-gray-100 my-1" />
                <div className="px-3 py-2">
                  <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                    {t('tenant.otherTenants')}
                  </p>
                </div>
                {otherTenants.map((m) => (
                  <button
                    key={m.organizationId}
                    onClick={() => handleSwitch(m)}
                    className="w-full mx-1 px-3 py-2.5 rounded-lg flex items-center gap-3 hover:bg-gray-50 transition-colors"
                    style={{ width: 'calc(100% - 8px)' }}
                  >
                    <OrgAvatar branding={m.branding} name={m.tenantName} size="sm" />
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {m.tenantName}
                      </p>
                      <p className="text-xs text-gray-500">
                        {t(`roles.${m.role}`, { defaultValue: m.role })}
                      </p>
                    </div>
                  </button>
                ))}
              </>
            )}

            {/* Inviti in attesa */}
            {invites.length > 0 && (
              <>
                <div className="border-t border-gray-100 my-1" />
                <div className="px-3 py-2">
                  <p className="text-[11px] font-medium text-amber-500 uppercase tracking-wider flex items-center gap-1.5">
                    <Mail className="w-3 h-3" />
                    {t('tenant.pendingInvites')}
                  </p>
                </div>
                {invites.map((invite) => (
                  <div
                    key={invite.id}
                    className="mx-1 px-3 py-2.5 rounded-lg flex items-center gap-3 bg-amber-50/60"
                  >
                    <OrgAvatar branding={invite.branding} name={invite.tenantName} size="sm" />
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {invite.tenantName}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {t(`roles.${invite.role}`, { defaultValue: invite.role })}
                        {invite.invitedByName &&
                          ` · ${t('tenant.invitedBy', { name: invite.invitedByName })}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleDeclineInvite(invite)}
                        disabled={inviteBusy}
                        title={t('tenant.declineInvite')}
                        className="p-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 disabled:opacity-50 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleAcceptInvite(invite)}
                        disabled={inviteBusy}
                        title={t('tenant.acceptInvite')}
                        className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-100 disabled:opacity-50 transition-colors"
                      >
                        {acceptInvite.isPending && acceptInvite.variables === invite.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Gestione: porta alla schermata di scelta, che e' anche l'unico
                punto da cui si ripristina un'organizzazione eliminata. Senza
                questa voce ci si arriva solo con logout + login, e con una
                sola organizzazione nemmeno cosi' (il login la seleziona da se'). */}
            <div className="border-t border-gray-100 my-1" />
            <button
              onClick={() => {
                setIsOpen(false);
                // `switching` dice alla pagina di non rimbalzare subito allo
                // scheduler: ci stiamo andando apposta, con un tenant attivo.
                navigate('/select-tenant', { state: { switching: true } });
              }}
              className="w-full mx-1 px-3 py-2.5 rounded-lg flex items-center gap-3 hover:bg-gray-50 transition-colors"
              style={{ width: 'calc(100% - 8px)' }}
            >
              <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-4 h-4 text-gray-500" />
              </div>
              <span className="flex-1 text-sm font-medium text-gray-700 text-left">
                {t('tenant.manageOrgs')}
              </span>
              {deletedTenants.length > 0 && (
                <span
                  className="min-w-[18px] h-[18px] px-1 bg-gray-200 text-gray-600 text-[10px] font-bold rounded-full flex items-center justify-center flex-shrink-0"
                  title={t('tenant.restorableOrgs', { count: deletedTenants.length })}
                >
                  {deletedTenants.length}
                </span>
              )}
            </button>

            {/* Crea nuova */}
            <button
              onClick={() => {
                setIsOpen(false);
                setShowCreateModal(true);
              }}
              className="w-full mx-1 px-3 py-2.5 rounded-lg flex items-center gap-3 hover:bg-gray-50 transition-colors"
              style={{ width: 'calc(100% - 8px)' }}
            >
              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <Plus className="w-4 h-4 text-emerald-600" />
              </div>
              <span className="text-sm font-medium text-emerald-600">
                {t('tenant.createNew')}
              </span>
            </button>
          </div>
        )}
      </div>

      {/* Modal creazione */}
      <CreateTenantModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />
    </>
  );
}
